import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import apiService from "../../services/ApiService";
import { AttendanceData } from "../../model/AttendanceData";

interface AttendanceState {
  records: AttendanceData[];
  isLoading: boolean;
  error: string | null;
}

const initialState: AttendanceState = {
  records: [],
  isLoading: false,
  error: null,
};

export const fetchAttendance = createAsyncThunk<
  AttendanceData[],
  { classId: string; from?: string; to?: string },
  { rejectValue: string }
>("attendance/fetchAttendance", async (params, { rejectWithValue }) => {
  const res = await apiService.queryAttendance(params);
  if (res.success && res.data) return res.data as AttendanceData[];
  return rejectWithValue(res.message || "Failed to fetch attendance");
});

const attendanceSlice = createSlice({
  name: "attendance",
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearRecords: (state) => {
      state.records = [];
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchAttendance.pending, (state) => {
        state.isLoading = true;
        state.error = null;
      })
      .addCase(fetchAttendance.fulfilled, (state, action) => {
        state.isLoading = false;
        state.records = action.payload;
      })
      .addCase(fetchAttendance.rejected, (state, action) => {
        state.isLoading = false;
        state.error = action.payload ?? "Failed to fetch attendance";
      });
  },
});

export const { clearError, clearRecords } = attendanceSlice.actions;

export const selectAttendance = (state: { attendance: AttendanceState }) =>
  state.attendance.records;
export const selectAttendanceLoading = (state: { attendance: AttendanceState }) =>
  state.attendance.isLoading;
export const selectAttendanceError = (state: { attendance: AttendanceState }) =>
  state.attendance.error;

export default attendanceSlice.reducer;
